import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, ApiError, unwrap } from './api';

/** Data arrives from the plants about once a minute, so polling faster shows nothing new. */
export const REFRESH_MS = 60_000;

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await unwrap(api.GET('/me'));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export const useSignInMethods = () =>
  useQuery({ queryKey: ['sign-in-methods'], queryFn: () => unwrap(api.GET('/sign-in-methods')), staleTime: Infinity });

export const usePlants = () =>
  useQuery({ queryKey: ['plants'], queryFn: async () => (await unwrap(api.GET('/plants'))).data, refetchInterval: REFRESH_MS });

export const usePlant = (plant: string) =>
  useQuery({
    queryKey: ['plant', plant],
    queryFn: () => unwrap(api.GET('/plants/{plant}', { params: { path: { plant } } })),
    refetchInterval: REFRESH_MS,
  });

export const useDatapoints = (plant: string) =>
  useQuery({
    queryKey: ['datapoints', plant],
    queryFn: async () => (await unwrap(api.GET('/plants/{plant}/datapoints', { params: { path: { plant } } }))).data,
    staleTime: 5 * 60_000,
  });

export const useLatest = (plant: string) =>
  useQuery({
    queryKey: ['latest', plant],
    queryFn: async () => (await unwrap(api.GET('/plants/{plant}/latest', { params: { path: { plant } } }))).data,
    refetchInterval: REFRESH_MS,
  });

export function useHistory(plant: string, datapoints: string[], from: number, to: number, live: boolean, maxPoints: number) {
  return useQuery({
    queryKey: ['history', plant, datapoints.join(','), live ? `live:${to - from}` : `${from}-${to}`, maxPoints],
    queryFn: () => {
      // Live ranges ("last 24 h") move with the clock on every refresh.
      const end = live ? Date.now() : to;
      const start = live ? end - (to - from) : from;
      return unwrap(
        api.GET('/plants/{plant}/history', {
          params: {
            path: { plant },
            query: { datapoints: datapoints.join(','), from: new Date(start).toISOString(), to: new Date(end).toISOString(), maxPoints, resolution: 'auto' },
          },
        }),
      );
    },
    enabled: datapoints.length > 0,
    refetchInterval: live ? REFRESH_MS : false,
    placeholderData: (previous) => previous,
  });
}

export interface AlarmFilter {
  plant?: string;
  active?: 'true' | 'false';
  severity?: string;
}

export const useAlarms = (filter: AlarmFilter) =>
  useQuery({
    queryKey: ['alarms', filter],
    queryFn: async () => (await unwrap(api.GET('/alarms', { params: { query: filter } }))).data,
    refetchInterval: REFRESH_MS,
  });

export function useAlarmLog(filter: { plant?: string; from: string; to?: string }) {
  return useInfiniteQuery({
    queryKey: ['alarm-log', filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => unwrap(api.GET('/alarms/log', { params: { query: { ...filter, limit: 100, cursor: pageParam } } })),
    getNextPageParam: (last) => last.next ?? undefined,
    refetchInterval: REFRESH_MS,
  });
}

export const useAlarmSummary = (filter: { plant?: string; from: string }) =>
  useQuery({
    queryKey: ['alarm-summary', filter],
    queryFn: () => unwrap(api.GET('/alarms/summary', { params: { query: filter } })),
    refetchInterval: REFRESH_MS,
  });

export const useDevices = (plant: string, enabled = true) =>
  useQuery({
    queryKey: ['devices', plant],
    queryFn: async () => (await unwrap(api.GET('/plants/{plant}/devices', { params: { path: { plant } } }))).data,
    refetchInterval: REFRESH_MS,
    enabled,
  });

export const useMembers = (org: string | undefined) =>
  useQuery({
    queryKey: ['members', org],
    queryFn: () => unwrap(api.GET('/organizations/{org}/members', { params: { path: { org: org! } } })),
    enabled: !!org,
  });

export const useApiKeys = (org: string | undefined) =>
  useQuery({
    queryKey: ['api-keys', org],
    queryFn: () => unwrap(api.GET('/organizations/{org}/api-keys', { params: { path: { org: org! } } })),
    enabled: !!org,
  });

export const useAdminOrganizations = (enabled: boolean) =>
  useQuery({
    queryKey: ['admin-orgs'],
    queryFn: async () => (await unwrap(api.GET('/admin/organizations'))).data,
    refetchInterval: REFRESH_MS,
    enabled,
  });

/** Organizations the user manages (Members, API keys): theirs as admin, and all of them for Vardex admins. */
export function useManagedOrgs() {
  const me = useMe();
  const vardexAdmin = me.data?.platformRole === 'vardex_admin';
  const adminOrgs = useAdminOrganizations(vardexAdmin);
  const orgs = me.data
    ? [
        ...me.data.organizations.filter((o) => o.role === 'admin'),
        ...(vardexAdmin ? (adminOrgs.data ?? []).filter((o) => !me.data!.organizations.some((m) => m.id === o.id)) : []),
      ]
    : [];
  return { me, vardexAdmin, orgs };
}

export const useInvitation = (id: string) =>
  useQuery({
    queryKey: ['invitation', id],
    queryFn: () => unwrap(api.GET('/invitations/{id}', { params: { path: { id } } })),
    retry: false,
  });
